import { createIntl, createIntlCache } from 'react-intl';

import getUserRecommendations from '@atlaskit/smart-user-picker/recommendation-client';
import type { RecommendationRequest, RestrictionFilter } from '@atlaskit/smart-user-picker/types';
import type { OptionData } from '@atlaskit/user-picker/types';
import type { User } from '@atlaskit/user-picker/types';

import { parseContextAri } from '../../custom-ui/iframe/utils/parseContextAri';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

// `ProductAttributes` is not exported from the package root, so derive it from
// the exported `RecommendationRequest` type instead.
type ProductAttributes = NonNullable<RecommendationRequest['context']>['productAttributes'];

// Bitbucket User Recommendations does not support a generic contextType
const BITBUCKET_FIELD_ID = 'BitbucketMentions';

export type FetchUserRecommendationsInput = {
	/** The base URL of the site, e.g. `hello.atlassian.net`. */
	baseUrl?: string;
	/** Id of the closest child context object, e.g. commentId. Used for analytics. */
	childObjectId?: string;
	/** Container context id, e.g. Jira projectId / Confluence spaceId / Bitbucket repositoryId. */
	containerId?: string;
	/**
	 * The picker's field id. Maps to `context.contextType`. For bitbucket this is
	 * overridden with the fixed `BitbucketMentions` value.
	 */
	fieldId: string;
	/** Whether to include groups in the resultset. Only supported for Confluence. */
	includeGroups?: boolean;
	/** Whether to include non-licensed users in the resultset. */
	includeNonLicensedUsers?: boolean;
	/** Whether to include teams in the resultset. */
	includeTeams?: boolean;
	/** Whether to include users in the resultset. Defaults to true. */
	includeUsers?: boolean;
	/** Whether the query should be treated as an email search. */
	isEmail?: boolean;
	/** When true, URS returns only userbase-aligned teams (teams synced to Identity). */
	isTeamSyncedToGroupDirectoryFilter?: boolean;
	/** Maximum number of results to return. Defaults to 10. */
	maxNumberOfResults?: number;
	/** Id of the closest context object, e.g. issueId / pageId / pullRequestId. Used for analytics. */
	objectId?: string;
	/** Product-specific attributes matching the current product (Confluence/Jira/Bitbucket). */
	productAttributes?: ProductAttributes;
	/** Product identifier, e.g. `jira` | `confluence` | `bitbucket` | `people`. */
	productKey: string;
	/** The search query string typed by the user. */
	query: string;
	/** Restricts recommendations to specific users and/or groups. */
	restrictTo?: RestrictionFilter;
	/** Optional URS search-query filter. */
	searchQueryFilter?: string;
	/** Session id for the picker session. Used for analytics. */
	sessionId?: string;
	/** When true, only verified teams are returned (applies when includeTeams is true). */
	verifiedTeams?: boolean;
};

/**
 * A single user option returned across the bridge. This is a trimmed subset of the
 * `OptionData` produced by `getUserRecommendations` — only the fields below are
 * returned; the rest are dropped.
 */
export type FetchUserRecommendationsResponseItem = Pick<
	OptionData,
	| 'appType'
	| 'avatarUrl'
	| 'id'
	| 'includeTeamsUpdates'
	| 'isDisabled'
	| 'name'
	| 'title'
	| 'tooltip'
	| 'type'
	| 'verified'
>;

export type FetchUserRecommendationsResponse = FetchUserRecommendationsResponseItem[];

type FetchUserRecommendationsContext = MakeBridgeContext<'contextIds' | 'viewContext'>;

export type FetchUserRecommendationsPayload = { query?: string };

export const fetchUserRecommendations: TBridgeMethod<
	FetchUserRecommendationsContext,
	FetchUserRecommendationsPayload,
	FetchUserRecommendationsResponse
> = async ({ contextIds, viewContext }, { query }) => {
	const { accountId, cloudId, locale } = viewContext;

	const productKey = parseContextAri(contextIds[0] ?? '').resourceOwner;
	const recommendationsRequest = {
		context: {
			// Bitbucket User Recommendations does not support a generic contextType.
			contextType: productKey === 'bitbucket' ? BITBUCKET_FIELD_ID : 'filter',
			// If principalId === 'Context', upstream attempts to use other context
			// (e.g. the request headers) to discover the principal id.
			principalId: accountId ?? 'Context',
			productKey: productKey === 'jira' || productKey === 'confluence' ? productKey : 'people',
			siteId: cloudId ?? '',
		},
		includeUsers: true,
		includeGroups: false,
		maxNumberOfResults: 100,
		query: query ?? '',
	};

	const intl = createIntl({ locale: locale ?? 'en' }, createIntlCache());

	const users = (await getUserRecommendations(recommendationsRequest, intl)) as User[];

	return users.map((user) => ({
		appType: user.appType,
		avatarUrl: user.avatarUrl,
		email: user.email,
		id: user.id,
		includeTeamsUpdates: user.includeTeamsUpdates,
		isDisabled: user.isDisabled,
		name: user.name,
		title: user.title,
		tooltip: user.tooltip,
		type: user.type,
		verified: user.verified,
	}));
};
